import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

type Avaliacao = {
  id: number;
  nota: number;
  comentario: string;
  usuarioNome: string;
  usuarioFotoUrl?: string;
  pacoteId: number;

  // Fotos gerais (apenas IDs, igual ao DTO)
  fotos?: number[];

  // Fotos vinculadas a pontos do tour
  fotosPorPonto?: {
    fotoId: number;
    ponto: string;
    url: string;
  }[];
};


type ReviewsProps = {
  pacoteId: number;
};

const Reviews: React.FC<ReviewsProps> = ({ pacoteId }) => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);


  useEffect(() => {
    const fetchAvaliacoes = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/avaliacoes/pacote/${pacoteId}`);
        setAvaliacoes(res.data);
      } catch (err) {
        console.error("Erro ao carregar avaliações:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAvaliacoes();
  }, [BASE_URL, pacoteId]);

  const mediaNota = avaliacoes.length
    ? (avaliacoes.reduce((acc, a) => acc + (a.nota || 0), 0) / avaliacoes.length).toFixed(1)
    : 0;

  return (
    <>
      <div id="reviews">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
          <h5 className="mb-3 fs-18">Reviews ({avaliacoes.length})</h5>
          <Link to="#" data-bs-toggle="modal" data-bs-target="#add_review" className="btn btn-primary btn-md mb-3">
            <i className="isax isax-edit-2 me-1"></i>Write a Review
          </Link>
        </div>

        {loading ? (
          <p>Carregando reviews...</p>
        ) : avaliacoes.length === 0 ? (
          <p>Nenhuma avaliação encontrada.</p>
        ) : (
          <div className="row">
            <div className="col-md-6 d-flex">
              <div className="rating-item bg-light-200 text-center flex-fill mb-3">
                <h6 className="fw-medium mb-3">Customer Reviews & Ratings</h6>
                <h5 className="display-6">{mediaNota} / 5.0</h5>
                <div className="d-inline-flex align-items-center justify-content-center mb-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <i
                      key={i}
                      className={`ti ti-star-filled ${i < Math.round(Number(mediaNota)) ? 'text-primary' : ''} me-1`}
                    ></i>
                  ))}
                </div>
                <p>Based on {avaliacoes.length} reviews</p>
              </div>
            </div>

            <div className="col-md-6 d-flex">
              <div className="card rating-progress shadow-none flex-fill mb-3">
                <div className="card-body">
                  {[5, 4, 3, 2, 1].map(star => {
                    const count = avaliacoes.filter(a => a.nota === star).length;
                    const percent = avaliacoes.length ? (count / avaliacoes.length) * 100 : 0;
                    return (
                      <div key={star} className="d-flex align-items-center mb-2">
                        <p className="me-2 text-nowrap mb-0">{star} Star Ratings</p>
                        <div className="progress w-100" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
                          <div className="progress-bar bg-primary" style={{ width: `${percent}%` }}></div>
                        </div>
                        <p className="progress-count ms-2">{count}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista de avaliações */}
        {avaliacoes.map(av => (
          <div key={av.id} className="card review-item shadow-none mb-3">
            <div className="card-body p-3">
              <div className="review-info">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <div className="d-flex align-items-center mb-2">
                    <span className="avatar avatar-lg me-3 flex-shrink-0">
                      <img
                        src={av.usuarioFotoUrl ? `${BASE_URL}${av.usuarioFotoUrl}` : "/assets/img/users/default-avatar.png"}
                        className="rounded-circle"
                        alt={av.usuarioNome}
                      />
                    </span>
                    <div>
                      <h6 className="fs-16 fw-medium mb-1">{av.usuarioNome}</h6>
                      <div className="d-flex align-items-center flex-wrap date-info">
                        <p className="fs-14 mb-1 d-inline-flex align-items-center">
                          <span className="badge badge-warning badge-xs text-gray-9 fs-13 fw-medium me-2 d-inline-flex align-items-center">
                            <i className="isax isax-star" />{av.nota}
                          </span>
                          <span style={{ color: '#333', fontSize: '0.95rem', lineHeight: 1.5 }}>
                            {av.comentario || ""}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fotos da review */}
                {((av.fotosPorPonto?.length ?? 0) > 0 || (av.fotos?.length ?? 0) > 0) && (
                  <div className="d-flex align-items-center flex-wrap mt-3 mb-2">

                    {/* Fotos por ponto */}
                    {av.fotosPorPonto?.map(f => (
                      <div
                        key={`ponto-${f.fotoId}`}
                        className="me-3 mb-3 position-relative"
                        style={{ width: "90px", height: "90px", cursor: "pointer" }}
                        onClick={() => {
                          // 🔑 Junta fotos gerais + fotos por ponto
                          const todasFotos = [
                            ...(av.fotos?.map(fid => ({ src: `${BASE_URL}/uploads/${fid}` })) || []),
                            ...(av.fotosPorPonto?.map(fp => ({ src: `${BASE_URL}${fp.url}` })) || []),
                          ];

                          // índice da foto clicada dentro do array unificado
                          const index = todasFotos.findIndex(img => img.src === `${BASE_URL}${f.url}`);

                          setLightboxSlides(todasFotos);
                          setLightboxIndex(index >= 0 ? index : 0);
                          setLightboxOpen(true);
                        }}
                      >
                        <img
                          src={`${BASE_URL}${f.url}`}
                          alt={f.ponto || "foto do ponto"}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                        {/* Overlay com o nome do ponto */}
                        <div
                          className="position-absolute bottom-0 start-0 w-100 text-center text-white px-1"
                          style={{
                            background: "rgba(0,0,0,0.55)",
                            fontSize: "0.7rem",
                            borderBottomLeftRadius: "8px",
                            borderBottomRightRadius: "8px",
                          }}
                        >
                          {f.ponto}
                        </div>
                      </div>
                    ))}

                    {/* Fotos gerais */}
                    {av.fotos?.map(fid => (
                      <div
                        key={`foto-${fid}`}
                        className="me-3 mb-3"
                        style={{ width: "90px", height: "90px", cursor: "pointer" }}
                        onClick={() => {
                          // 🔑 Junta fotos gerais + fotos por ponto
                          const todasFotos = [
                            ...(av.fotos?.map(fid2 => ({ src: `${BASE_URL}/uploads/${fid2}` })) || []),
                            ...(av.fotosPorPonto?.map(fp => ({ src: `${BASE_URL}${fp.url}` })) || []),
                          ];

                          const index = todasFotos.findIndex(img => img.src === `${BASE_URL}/uploads/${fid}`);

                          setLightboxSlides(todasFotos);
                          setLightboxIndex(index >= 0 ? index : 0);
                          setLightboxOpen(true);
                        }}
                      >
                        <img
                          src={`${BASE_URL}/uploads/${fid}`}
                          alt="foto da avaliação"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={lightboxSlides}
          index={lightboxIndex}
        />

      </div>

      {/* Modal to add a review */}
      <div className="modal fade" id="add_review">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center justify-content-between">
              <h5 className="mb-0">How to Write a Review</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="mb-3">
                To leave a review, please follow these steps:
              </p>
              <ol className="ps-3 mb-3" style={{ lineHeight: "1.6" }}>
                <li>1.Go to your <strong>User Dashboard</strong>.</li>
                <li>2.Navigate to the <strong>My Bookings</strong> section.</li>
                <li>3.Select the service or tour you want to review.</li>
                <li>4.Inside the booking card, click the <strong>Review</strong> button.</li>
              </ol>
              <p className="text-muted fs-13">
                This will allow you to rate, write comments, and even upload photos
                from your experience. 🎉
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reviews;